import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { apiClient } from '@/infrastructure/api/client'
import { Button } from '@/ui/components/common/Button'
import { Input } from '@/ui/components/common/Input'
import { Label } from '@/ui/components/common/Label'
import type { User } from '@/domains/types'

const makeTotpSchema = (translate: (key: string, opts?: Record<string, unknown>) => string) =>
  z.object({
    totp: z
      .string()
      .min(6, translate('validation.required'))
      .max(6, translate('validation.required'))
      .regex(/^\d{6}$/, translate('validation.required')),
  })

type TotpFormData = z.infer<ReturnType<typeof makeTotpSchema>>

export interface TotpSectionProps {
  /** Short-lived token returned by `/auth/login` when 2FA is required. */
  tempToken: string
  /** Called when the TOTP exchange returns an `accessToken` and a user. */
  onAuthenticated: (
    user: Omit<User, 'createdAt' | 'updatedAt'>,
    accessToken: string,
    refreshToken: string,
  ) => void
  /** "Back" link handler — typically clears the temp token in the parent. */
  onBack: () => void
}

/**
 * Step 2 of the public login flow: 6-digit TOTP form. Owns its own form state,
 * schema and submit handler against `/auth/2fa/login`, and exposes a "back"
 * affordance to return to the credentials step.
 */
export function TotpSection({ tempToken, onAuthenticated, onBack }: TotpSectionProps) {
  const { t } = useTranslation()
  const totpSchema = useMemo(() => makeTotpSchema(t), [t])

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<TotpFormData>({ resolver: zodResolver(totpSchema) })

  const onSubmit = async (data: TotpFormData) => {
    try {
      const response = await apiClient.post<{
        accessToken: string
        refreshToken: string
        user: Omit<User, 'createdAt' | 'updatedAt'>
      }>('/auth/2fa/login', { tempToken, totp: data.totp })

      onAuthenticated(response.data.user, response.data.accessToken, response.data.refreshToken)
    } catch {
      setError('root', { message: t('common.error') })
    }
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-4">
      {errors.root && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md px-3 py-2">
          {errors.root.message}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="totp">{t('auth.two_factor_code_label')}</Label>
          <Input
            id="totp"
            {...register('totp')}
            className="mt-1 text-center tracking-widest text-lg"
            placeholder={t('auth.two_factor_code_placeholder')}
            maxLength={6}
            autoComplete="one-time-code"
            inputMode="numeric"
            autoFocus
          />
          {errors.totp && <p className="text-destructive text-xs mt-1">{errors.totp.message}</p>}
        </div>

        <Button type="submit" className="w-full" isLoading={isSubmitting}>
          {t('auth.two_factor_verify_button')}
        </Button>
      </form>

      <button
        type="button"
        onClick={onBack}
        className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        ← {t('auth.two_factor_back')}
      </button>
    </div>
  )
}
